import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import type { Collection, SourceSummary } from "../library/types";

export function CollectionsAdminPage() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [sources, setSources] = useState<SourceSummary[]>([]);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    void api.get<{ collections: Collection[] }>("/api/v1/collections").then((r) => setCollections(r.collections));
    void api.get<{ sources: SourceSummary[] }>("/api/v1/sources").then((r) => setSources(r.sources));
  }, []);

  useEffect(() => reload(), [reload]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/api/v1/collections", { name });
    setName("");
    reload();
  }

  async function onDelete(id: string) {
    await api.delete(`/api/v1/collections/${id}`);
    if (selected === id) setSelected(null);
    reload();
  }

  function openCollection(id: string) {
    setSelected(id);
    const membership = new Set<string>();
    // The list endpoint filters by collectionId server-side; fetch this collection's members directly.
    void api.get<{ sources: SourceSummary[] }>(`/api/v1/sources?collectionId=${id}`).then((r) => {
      for (const s of r.sources) membership.add(s.id);
      setAssigned(membership);
    });
  }

  function toggle(sourceId: string) {
    setAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) next.delete(sourceId);
      else next.add(sourceId);
      return next;
    });
  }

  async function onSave() {
    if (!selected) return;
    setSaving(true);
    await api.put(`/api/v1/collections/${selected}/sources`, { sourceIds: [...assigned] });
    setSaving(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.8rem", fontSize: "1.35rem", fontWeight: 600 }}>Sammlungen</h1>
        <form onSubmit={(e) => void onCreate(e)} noValidate style={{ display: "flex", gap: "0.5rem" }}>
          <input
            type="text"
            placeholder="Neue Sammlung …"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ flex: 1, padding: "0.6rem 0.8rem", borderRadius: "var(--pw-radius-sm)", border: "1px solid var(--pw-color-surface-border)", background: "var(--pw-color-surface)", color: "var(--pw-color-text)" }}
          />
          <button type="submit" className="pw-button pw-button--primary">
            Anlegen
          </button>
        </form>
      </section>

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <section className="pw-card" style={{ flex: "1", minWidth: "240px" }}>
          <p style={{ margin: "0 0 0.6rem", fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--pw-color-text-muted)" }}>Sammlungen</p>
          {collections.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0", borderBottom: "1px solid var(--pw-color-surface-flat-border)" }}>
              <button
                type="button"
                onClick={() => openCollection(c.id)}
                style={{ flex: 1, textAlign: "left", background: "none", border: "none", color: selected === c.id ? "var(--pw-color-accent-strong)" : "var(--pw-color-text)", cursor: "pointer", fontFamily: "inherit", fontSize: "0.9rem", padding: 0 }}
              >
                {c.name}
              </button>
              <button type="button" className="pw-button" onClick={() => void onDelete(c.id)}>
                Löschen
              </button>
            </div>
          ))}
        </section>

        {selected && (
          <section className="pw-card" style={{ flex: "2", minWidth: "280px" }}>
            <p style={{ margin: "0 0 0.6rem", fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--pw-color-text-muted)" }}>
              Quellen zuordnen
            </p>
            <div style={{ maxHeight: "360px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              {sources.map((s) => (
                <label key={s.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.88rem", padding: "0.3rem 0" }}>
                  <input type="checkbox" checked={assigned.has(s.id)} onChange={() => toggle(s.id)} />
                  {s.title}
                </label>
              ))}
            </div>
            <button type="button" className="pw-button pw-button--primary" onClick={() => void onSave()} disabled={saving} style={{ marginTop: "0.8rem" }}>
              {saving ? "Speichert …" : "Speichern"}
            </button>
          </section>
        )}
      </div>
    </div>
  );
}
